import { isUrl } from '@ad/utils/url';
import { IVersionProperty, IFontStyle, IPadding, IBorder, IShadow, ITextShadow, IFeed, IImageAsset, OneOfElementPropertyKeys, WidgetPropertyKeys, PropertyValue, propertyToUnitMap, widgetPropertyToUnitMap } from '@studio/domain';
import { Color } from '../color';
import { parseColor } from '../color.utils';
import { deserializeVersionedText, serializeInlineStyledText, serializeVersionedText } from './text-serializer';

/**
 * Clone value by serializing and deserializing. Compared to a deep clone this will also work with color properties
 * @param propertyName
 * @param value
 */
export function clonePropertyValue(propertyName: string, value: any): PropertyValue | undefined {
    return deserializePropertyValue(propertyName, serializePropertyValue(propertyName, value));
}

export function deserializePropertyValue(propertyName: OneOfElementPropertyKeys | WidgetPropertyKeys | string, value: any, isKeyFrame: boolean = false): PropertyValue | undefined {

    const unit = propertyToUnitMap[propertyName] || widgetPropertyToUnitMap[propertyName];

    if (value === null) {
        value = undefined;
    }

    // Should maybe not affect all types?
    if (typeof value === 'string' && isKeyFrame) {
        return value;
    }
    switch (unit) {
        case 'angle':
            return parseFloat(value!) * Math.PI / 180;
        case 'integer':
            return parseInt(value, 10);
        case 'number':
        case 'px':
            return parseFloat(value!);
        case 'boolean':
            return typeof value === 'string'
                ? value.toLowerCase() === 'true'
                : value;
        case 'FontStyle':
            if (value === undefined) {
                return undefined;
            }
            const fontStyle = value.split(' ') as IFontStyle;
            let src = fontStyle[3];
            try {

                // Support old font url that where encoded with base64 without data prefix.
                //
                // Note: that exported HTML creatives can have base64 encoded fonts which begins with data prefix.
                if (!src.startsWith('data') && !isUrl(src)) {
                    if (typeof window !== 'undefined') {
                        src = atob(src);
                    }
                    else {
                        src = Buffer.from(src, 'base64').toString();
                    }
                }
            }
            // eslint-disable-next-line no-empty
            catch { }

            return {
                id: fontStyle[0],
                weight: parseInt(fontStyle[1], 10),
                style: fontStyle[2],
                src,
                fontFamilyId: fontStyle[4],
            };
        case 'ImageAsset':
            const imageAsset = value.split(' ');
            return {
                id: imageAsset[0],
                url: imageAsset[1],
                width: imageAsset[2],
                height: imageAsset[3]
            };
        case 'Padding':
            const padding = value!.split(' ') || [];
            return {
                top: padding[0] ? parseFloat(padding[0]) : 0,
                right: padding[1] ? parseFloat(padding[1]) : 0,
                bottom: padding[2] ? parseFloat(padding[2]) : 0,
                left: padding[3] ? parseFloat(padding[3]) : 0,
            } as IPadding;
        case 'Border':
            const border = value!.split(' ');
            return border.length > 1 ? {
                thickness: parseInt(border[0], 10),
                style: border[1],
                color: border[2]
            } as IBorder : undefined;
        case 'Shadow[]':
            const shadows = value ? value!.split(/,(?![^\(]*\))/) : undefined;
            return shadows ? shadows.map(s => {
                const shadow = s.split(' ');
                return {
                    offsetX: parseInt(shadow[0], 10),
                    offsetY: parseInt(shadow[1], 10),
                    blur: parseInt(shadow[2], 10),
                    spread: parseInt(shadow[3], 10),
                    color: parseColor(shadow[4])
                } as IShadow;
            }) : undefined;
        case 'TextShadow[]':
            if (Array.isArray(value)) {
                return value;
            }
            const textShadows = value ? value!.split(/,(?![^\(]*\))/) : undefined;
            return textShadows ? textShadows.map(s => {
                const shadow = s.split(' ');
                return {
                    offsetX: parseInt(shadow[0], 10),
                    offsetY: parseInt(shadow[1], 10),
                    blur: parseInt(shadow[2], 10),
                    color: parseColor(shadow[3])
                } as ITextShadow;
            }) : undefined;
        case 'Color':
            return parseColor(value);
        case 'Feed':
        case 'variable':

            try {
                const feed = JSON.parse(value);
                if (!('path' in feed)) {
                    // Continue to old deserialization
                    throw new Error();
                }

                return feed;
            }
            catch (_) {
                const feed = value.split(' ');
                return {
                    id: feed[0],
                    path: feed[1],
                    step: {
                        start: parseInt(feed[2], 10),
                        size: parseInt(feed[3], 10),
                        occurrence: feed[4]
                    },
                    fallback: feed[5]
                } as IFeed;
            }
        case 'string[]':
        case 'CustomProperty[]':
            let decodedValue;
            if (typeof window !== 'undefined') {
                decodedValue = JSON.parse(atob(value));
            }
            else {
                decodedValue = Buffer.from(value, 'base64').toString();
            }
            return decodedValue;
        case 'FilterMap':
        case 'ImageSettings':
        case 'Transition':
        case 'State[]':
        case 'Animation[]':
        case 'Action[]':
            if (typeof Array.isArray(value)) {
                return value.length
                    ? JSON.parse(value)
                    : value;
            }
            return JSON.parse(value);
        case 'text': {

            // Elements doesn't have "content" and value will instead be fetched from Version.properties
            if (value === undefined) {
                return undefined;
            }
            return deserializeVersionedText(value);
        }
        default:
            return value;
    }
}

/**
 * Serialize propertyValue to a string
 * @param propertyName
 * @param value
 */
export function serializePropertyValue(propertyName: string, value: any): string | undefined {
    let unit = propertyToUnitMap[propertyName] || widgetPropertyToUnitMap[propertyName];

    if (propertyName === 'id' ||
        propertyName === 'kind' ||
        propertyName === 'feededImageReference'
    ) {
        unit = 'string';
    }

    switch (unit) {
        case 'angle':
            return `${Math.round(value * 180 / Math.PI)}`;
        case 'number':
            return `${value}`;
        case 'integer':
            return value;
        case 'string':
        case 'enum':
            return value;
        case 'boolean':
            return value.toString();
        case 'FontStyle':
            const fontStyle = value as IFontStyle;
            const src = fontStyle.src;
            return `${fontStyle.id} ${fontStyle.weight} ${fontStyle.style} ${src} ${fontStyle.fontFamilyId}`;
        case 'ImageAsset':
            const imageAsset = value as IImageAsset;
            return `${imageAsset.id} ${imageAsset.url} ${imageAsset.width} ${imageAsset.height}`;
        case 'Border':
            const border = value as IBorder;
            return border ? `${border.thickness} ${border.style} ${border.color}` : '';
        case 'Padding':
            const padding = value as IPadding;
            return `${padding.top} ${padding.right || ''} ${padding.bottom || ''} ${padding.left || ''}`;
        case 'Shadow[]':
            const shadows = value as IShadow[];
            return shadows ? shadows.map(s => `${s.offsetX} ${s.offsetY} ${s.blur} ${s.spread} ${s.color}`).join(',') : '';
        case 'TextShadow[]':
            return value ? value.map((s) => `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.color}`).join(',') : '';
        case 'Color':
            const color = value as Color;
            if (!color) {
                return '';
            } else {
                return color.toString();
            }
        case 'inline-styled-text':
            return serializeInlineStyledText(value);
        case 'text':
            return serializeVersionedText(value);
        case 'variable':
        case 'Feed':
            if (typeof value === 'object') {
                return JSON.stringify(value);
            }
            return value;
        case 'string[]':
        case 'CustomProperty[]':
            let encodedValue;
            if (typeof window !== 'undefined') {
                encodedValue = btoa(JSON.stringify(value));
            }
            else {
                encodedValue = Buffer.from(value).toString('base64');
            }
            return encodedValue;
        case 'Transition':
        case 'FilterMap':
        case 'State[]':
        case 'Animation[]':
        case 'Action[]':
        case 'ImageSettings':
            return JSON.stringify(value);
        default:
            return value;
    }
}

export function deserializeVersionProperties(epv: IVersionProperty[] = []): IVersionProperty[] {
    const properties = epv.map(p => {
        const prop: any = {
            id: p.id as string,
            name: p.name as string,
            value: deserializePropertyValue(p.name, p.value)
        };
        return prop;
    });
    return properties;
}

export function serializeVersionProperties(vp: IVersionProperty[] = []): any[] {
    const properties = vp.map(p => ({
        id: p.id as string,
        name: p.name as string,
        value: serializePropertyValue(p.name, p.value)
    }));
    return properties;
}
